// Story 11.6 — ErrorShake primitive.
// `useErrorShake()` returns a local ref + a `shake()` trigger that fires a
// one-shot GSAP oscillation on the attached element. Reduced-motion short-
// circuit is module-scope (Pattern 5) so the hook never imports gsap when
// the user prefers reduced motion. Amplitude/duration follow epics.md
// line 3292: ±2px, 3 oscillations, 300ms, decaying via power2.inOut.

import { useCallback, useRef, type RefObject } from 'react';
import { loadGsap, prefersReducedMotion } from '../../../lib/gsap-setup.js';

export interface UseErrorShakeResult<E extends HTMLElement = HTMLElement> {
  ref: RefObject<E | null>;
  shake: () => void;
}

export function useErrorShake<E extends HTMLElement = HTMLElement>(): UseErrorShakeResult<E> {
  const ref = useRef<E | null>(null);

  const shake = useCallback(() => {
    if (prefersReducedMotion) return;
    const el = ref.current;
    if (!el) return;
    void loadGsap()
      .then((gsap) => {
        // ±2px over 5 repeats @ 50ms with yoyo ≈ 6 tween segments × 50ms = 300ms.
        // power2.inOut softens the endpoints, approximating the "decaying amplitude"
        // in the visual spec without a bespoke onUpdate callback.
        gsap.fromTo(
          el,
          { x: -2 },
          {
            x: 0,
            duration: 0.05,
            repeat: 5,
            yoyo: true,
            ease: 'power2.inOut',
            clearProps: 'x',
          },
        );
      })
      .catch(() => undefined);
  }, []);

  return { ref, shake };
}
